import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../components/ui/PageHeader.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { superAdminApi } from '../../api/superAdmin.js';
import { MonthPager, currentMonthTaipei } from '../../components/SuperAdmin/MonthPager.jsx';
import { coverFromSeed, initialsOf } from '../../components/IvrAgents/coverArt.js';
import { ChevronDown } from 'lucide-react';

const SYM = { TWD: 'NT$', THB: '฿', USD: '$', JPY: '¥', KRW: '₩', VND: '₫', MYR: 'RM', CNY: '¥' };
const money = (n, cur) => `${SYM[cur] || cur} ${Number(n || 0).toLocaleString()}`;

/**
 * /admin/billing — 每月應收總覽（商用帳號）
 * 帳單引擎：角色建置費（開戶月、每角色）+ 坐席月費 + CS 超額分鐘；幣別 = 帳號 billingCurrency。
 * 商用帳號 = 企業藍卡片（同超管中心視覺）：可開角色數 / 席次 / 分鐘 / 應收金額 + 展開計費明細。
 */
export default function ReceivablesPage() {
    const { t } = useTranslation('superAdmin');
    const [month, setMonth] = useState(currentMonthTaipei());
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState({});   // uid → bool

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        superAdminApi.billingOverview(month, 'business')
            .then((d) => { if (!cancelled) { setData(d); setError(null); } })
            .catch((e) => { if (!cancelled) setError(e); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [month]);

    const statements = (data?.statements || []).filter((s) => !s.error);
    const currencies = Object.entries(data?.totalByCurrency || {});

    return (
        <div>
            <PageHeader
                eyebrow="super admin · receivables"
                title={t('billing.title')}
                description={t('billing.description')}
                actions={<MonthPager month={month} onChange={setMonth} />}
            />
            {error && (
                <div className="mb-6 px-4 py-3 rounded-md border border-danger-500/30 bg-danger-100/40 text-sm text-danger-500">
                    {String(error.message || error)}
                </div>
            )}

            {/* 應收總額（分幣別） */}
            <div className="mb-10 flex items-stretch divide-x divide-cream-200 border-y border-cream-200">
                {currencies.length > 0 ? currencies.map(([cur, total]) => (
                    <div key={cur} className="flex-1 px-6 py-4 first:pl-1">
                        <div className="tnum heading-display text-3xl font-semibold text-ink-900 leading-none">{money(total, cur)}</div>
                        <div className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-ink-400">{t('billing.totalIn', { cur })}</div>
                    </div>
                )) : (
                    <div className="flex-1 px-1 py-4">
                        <div className="tnum heading-display text-3xl font-semibold text-ink-300 leading-none">0</div>
                        <div className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-ink-400">{t('billing.totalIn', { cur: '—' })}</div>
                    </div>
                )}
            </div>

            {loading ? (
                <div className="text-center py-20 text-ink-400 font-mono text-xs">Loading…</div>
            ) : statements.length === 0 ? (
                <div className="py-16 text-center">
                    <p className="text-sm text-ink-400 italic">{t('billing.empty')}</p>
                    <p className="mt-1 text-xs text-ink-300">{t('billing.emptyHint')}</p>
                </div>
            ) : (
                <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
                    {statements.map((s) => {
                        const cover = coverFromSeed(s.email || s.uid, 'business');
                        const rq = s.roleQuota;
                        const seats = s.seats;
                        return (
                            <Card key={s.uid} className="p-0 overflow-hidden" padded={false}>
                                {/* 企業藍封面條 */}
                                <div className="h-14 flex items-center gap-3 px-4 relative" style={{ background: cover.gradient }}>
                                    <span className="heading-display text-xl font-semibold" style={{ color: cover.fg }}>
                                        {initialsOf(s.email || s.uid)}
                                    </span>
                                    <span className="min-w-0 truncate text-sm font-medium text-white/95 drop-shadow-sm">{s.email || s.uid}</span>
                                    {s.deleted && (
                                        <span className="ml-auto shrink-0 font-mono text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-danger-500/90 text-white">
                                            {t('billing.deleted')}
                                        </span>
                                    )}
                                    <span className={`${s.deleted ? '' : 'ml-auto'} shrink-0 font-mono text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-white/85 text-ink-700`}>
                                        {t('role.business')}
                                    </span>
                                </div>
                                <div className="p-4">
                                    {/* 應收金額 */}
                                    <div className="tnum heading-display text-2xl font-semibold text-ink-900 leading-none">
                                        {money(s.total, s.currency)}
                                    </div>
                                    {/* 席次年收：週年月出帳一次；其餘月份提示哪月出帳 */}
                                    {s.seatBilling?.period === 'annual' && s.seatBilling.anchorMonth && (
                                        s.seatBilling.isAnniversaryMonth ? (
                                            <span className="mt-1.5 inline-block font-mono text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-accent-700/10 text-accent-700">
                                                {t('billing.seatAnnualDue')}
                                            </span>
                                        ) : (
                                            <div className="mt-1.5 text-[10px] text-ink-300">
                                                {t('billing.seatAnnualHint', { mm: s.seatBilling.anchorMonth.slice(5, 7) })}
                                            </div>
                                        )
                                    )}
                                    {/* 可開角色 / 席次 / 分鐘 */}
                                    <div className="mt-3 space-y-1.5 text-xs">
                                        <div className="flex items-center justify-between">
                                            <span className="text-ink-400">{t('billing.roles')}</span>
                                            <span className="tnum text-ink-700 font-medium">
                                                {rq ? <>AA {rq.plan_01 ?? 0} · CS {rq.plan_02 ?? 0}</> : '—'}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-ink-400">{t('billing.col.seats')}</span>
                                            <span className="tnum text-ink-700">
                                                {seats ? <>AA {seats.plan_01 ?? 0} · CS {seats.plan_02 ?? 0}</> : '—'}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-ink-400">{t('billing.col.minutes')}</span>
                                            <span className="tnum text-ink-700">{(s.usage?.minutesUsed ?? 0).toLocaleString()} min</span>
                                        </div>
                                        {/* 電話/網頁拆分（網頁按 webMinuteFactor 折算計入上面的折算分鐘） */}
                                        {(s.usage?.phoneMinutes != null || s.usage?.webMinutes != null) && (
                                            <div className="flex items-center justify-between text-[11px]">
                                                <span className="text-ink-300">{t('billing.minutesSplit')}</span>
                                                <span className="tnum text-ink-400">
                                                    {t('billing.minutesSplitValue', {
                                                        phone: (s.usage?.phoneMinutes ?? 0).toLocaleString(),
                                                        web: (s.usage?.webMinutes ?? 0).toLocaleString(),
                                                        factor: s.usage?.webMinuteFactor ?? 0.5,
                                                    })}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    {/* 計費明細（展開） */}
                                    <button
                                        onClick={() => setOpen((o) => ({ ...o, [s.uid]: !o[s.uid] }))}
                                        className="mt-3 pt-3 border-t border-cream-100 w-full flex items-center text-xs font-medium text-accent-700 hover:text-accent-500 transition focus-visible:outline-2 focus-visible:outline-accent-500"
                                    >
                                        {t('billing.detailToggle')}
                                        <ChevronDown size={13} className={`ml-auto text-ink-300 transition-transform ${open[s.uid] ? 'rotate-180' : ''}`} />
                                    </button>
                                    {open[s.uid] && (
                                        (s.lines || []).length === 0 ? (
                                            <p className="py-2 text-xs text-ink-400 italic">{t('billing.noLines')}</p>
                                        ) : (
                                            <div className="mt-1.5 divide-y divide-cream-100/80">
                                                {s.lines.map((l) => (
                                                    <div key={l.item} className="py-1.5">
                                                        <div className="text-[11px] text-ink-700 leading-snug">{l.label}</div>
                                                        <div className="flex items-center justify-between">
                                                            <span className="tnum text-[11px] text-ink-400">{l.qty} × {money(l.unitPrice, s.currency)}</span>
                                                            <span className="tnum text-xs text-ink-900">{money(l.amount, s.currency)}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )
                                    )}
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
